import hospitalityRules from "@content/rules/hospitality.json";
import type { IndicatorAnswer, ResolvedRouting } from "../help/types";

type HospitalityRules = {
  requiredModulesBeforeResources: string[];
  entryPaths: {
    protocolOnly: { modules: string[]; resourceCategories: string[] };
    possibleMinor: { modules: string[]; resourceCategories: string[] };
  };
  observationFlow: {
    order: string[];
    onSkipAll: { modules: string[]; resourceCategories: string[] };
  };
  defaultAfterObservations: {
    modules: string[];
    resourceCategories: string[];
  };
  mappings: Array<{
    id: string;
    whenAnyYes: string[];
    modules: string[];
    resourceCategories: string[];
  }>;
};

const rules = hospitalityRules as HospitalityRules;

function unique(items: string[]): string[] {
  return [...new Set(items)];
}

function unionRouting(
  parts: Array<{ modules: string[]; resourceCategories: string[] }>,
): Omit<ResolvedRouting, "hasAnyYes"> {
  return {
    modules: unique(parts.flatMap((p) => p.modules)),
    resourceCategories: unique(parts.flatMap((p) => p.resourceCategories)),
  };
}

export function getHospitalityObservationOrder(): string[] {
  return [...rules.observationFlow.order];
}

export function getHospitalityRequiredModulesBeforeResources(): string[] {
  return [...rules.requiredModulesBeforeResources];
}

export function resolveHospitalityFromAnswers(
  answers: Record<string, IndicatorAnswer>,
): ResolvedRouting {
  const yesKeys = Object.entries(answers)
    .filter(([, value]) => value === "yes")
    .map(([key]) => key);

  const base = rules.defaultAfterObservations;
  if (yesKeys.length === 0) {
    return {
      modules: [...base.modules],
      resourceCategories: [...base.resourceCategories],
      hasAnyYes: false,
    };
  }

  const matched = rules.mappings.filter((map) =>
    map.whenAnyYes.some((id) => yesKeys.includes(id)),
  );

  if (matched.length === 0) {
    return {
      modules: [...base.modules],
      resourceCategories: [...base.resourceCategories],
      hasAnyYes: true,
    };
  }

  const unioned = unionRouting([
    { modules: base.modules, resourceCategories: base.resourceCategories },
    ...matched,
  ]);
  return { ...unioned, hasAnyYes: true };
}

export function resolveHospitalityProtocolOnly(): ResolvedRouting {
  const path = rules.entryPaths.protocolOnly;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveHospitalitySkipAll(): ResolvedRouting {
  const path = rules.observationFlow.onSkipAll;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveHospitalityPossibleMinor(): ResolvedRouting {
  const path = rules.entryPaths.possibleMinor;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveHospitalityContactNow(): ResolvedRouting {
  return resolveHospitalityProtocolOnly();
}
